<template>
    <div class="main-checkout-confirmation">
		<!-- ORDER SUMMARY MOBILE -->
        <div class="mobile-order-summary-bar-wrapper">
            <div class="mobile-order-summary-bar">
                <details class="accordion">
                    <summary class="paragraph-feature accordion-title" aria-controls="order-summary">
                        <div>
                            <p class="paragraph-details text-rothcoRed">Show Order Summary</p>
                            <ChevronRedIcon class="icon"/>
                        </div>
                        <p class="price text-rothcoBlack">{{ summaryContent.formattedTotal }}
                        </p>

                    </summary>
                    <section class="order-confirmation">
                        <section class="summary-section">
                            <div class="summary-container">
                                <div class="container-header">
                                    <h3 id="order-summary-heading" class="headline-s">Order Summary</h3>
                                    <p class="text-rothcoBlack quantity" aria-label="Number of items in order">{{ props.order.count }} Items</p>
                                </div>
                                <dl>
                                    <dt class="paragraph-default" aria-label="Subtotal amount">Subtotal</dt>
                                    <dd class="paragraph-default">{{ summaryContent.formattedSubtotal }}</dd>
                                </dl>
                                <dl v-if="+props.order.discount > 0">
                                    <dt class="paragraph-default" aria-label="Discount amount">Discounts</dt>
                                    <dd class="paragraph-default text-rothcoRed font-italic">-{{ props.order.discount ? summaryContent.formattedDiscount : '$0.00' }}</dd>
                                </dl>
                                <dl>
                                    <dt class="paragraph-default" aria-label="Shipping cost">Shipping</dt>
                                    <dd class="paragraph-default">{{ summaryContent.formattedShipping }}</dd>
                                </dl>
                                <dl>
                                    <dt class="paragraph-default" aria-label="Taxes">Taxes</dt>
                                    <dd class="paragraph-default font-italic" v-if="props.order.sales_tax">{{ summaryContent.formattedTax }}</dd>
                                </dl>
                                <dl class="order-total">
                                    <dt class="title-l" aria-label="Total cost">Total</dt>
                                    <dd class="title-l">{{ summaryContent.formattedTotal }}</dd>
                                </dl>
                            </div>
                            <div class="order-products">
                                <h3 class="title-l">Items in Order</h3>
                                <OrderItemsD2CCheckout 
                                    :list-items="orderItems"
                                    :readonly="true"
                                />
                            </div>
                        </section>
                    </section>
                </details>
            </div>
        </div>
		<!-- CHECKOUT CONFIRMATION -->
        <section class="confirmation-section">
            <div class="confirmation-header">
                <div class="image-container">
                    <CheckmarkCircleIcon alt="checkmar circle" />
                </div>
                <div class="text-container">
                    <p class="headline-l text-rothcoBlack title">Thank you, {{ displayName }}!</p>
                    <p class="text-rothcoBlack paragraph-feature">Web Confirmation #{{ props.order.order_id }}</p>
                    <p v-if="props.order.code" class="text-rothcoBlack paragraph-feature">Order #{{ props.order.code }}</p>
                </div>
            </div>
            <div class="confirmed-order">
                <p class="text-rothcoBlack title-l">Your order is {{ (order.status ?? 'confirmed').toLowerCase() }}</p>
                <p class="paragraph-details">Your order confirmation # is {{ props.order.order_id }}. A copy of your order confirmation will be sent to {{ props.order.billing_email }}.</p>
                <div class="checkbox-container centered-checkbox-container">
                    <div style="display: flex; flex-direction: row; column-gap: 1.5rem;">
                        <div class="checkbox-row">
                            <input type="checkbox" id="email-subscribe" name="email-subscribe" v-model="emailSignup" />
                            <label class="paragraph-details text-rothcoText" for="email-subscribe">Email me with news and offers</label>
                        </div>
                        <div class="checkbox-row">
                            <input type="checkbox" id="sms-subscribe" name="sms-subscribe" v-model="smsSignup" />
                            <label class="paragraph-details text-rothcoText" for="sms-subscribe">Sign up for exclusive SMS updates</label>
                        </div>
                    </div>
                    <RothcoCta @click.prevent="saveSignup()" class="primary small">Save Settings</RothcoCta>
                </div>
            </div>
            <div class="order-details">
                <h3 class="title-l">Order Details</h3>
                <div class="details-container">
                    <div class="container-header">
                        <p>Contact Information</p>
                    </div>
                    <div>
                        <p>{{ displayName }}</p>
                        <p>{{ user.email ?? props.order.billing_email }}</p>
                    </div>
                </div>
                <div class="details-container noborder">
                    <div class="container-header">
                        <p>Shipping Information</p>
                    </div>
                    <div>
                        <p>{{ props.order.shipping_name }}</p>
                        <p>{{ props.order.shipping_address }}</p>
                        <p v-if="props.order.shipping_address_2">{{ props.order.shipping_address_2 }}</p>
                        <p>{{ props.order.shipping_city }}, {{ props.order.shipping_state }} {{ props.order.shipping_zip }}, {{ props.order.shipping_country }}</p>
                    </div>
                </div>
                <div class="details-container noborder">
                    <div class="container-header">
                        <p>Shipping Method</p>
                    </div>
                    <div>
                        <p>{{ props.order.shipping_method }}</p>
                    </div>
                </div>
                <div class="details-container">
                    <div class="container-header">
                        <p>Tracking Number</p>
                    </div>
                    <div>
                        <a v-if="props.order.tracking_number" target="_blank" :href="props.order.tracking_url">
                            {{ props.order.tracking_number }}
                        </a>
                        <p v-else>No tracking number set yet. Please check back later.</p>
                    </div>
                </div>
                <div class="details-container noborder">
                    <div class="container-header">
                        <p>Payment</p>
                    </div>
                    <div class="payment-method" v-if="props.order.billing_payment_method == 'MV'">
                        <MasterCardIcon v-if="/^master\s?card$/i.test(props.order.creditCard.cc_type)" alt="mastercard-logo"/>
                        <VisaIcon v-if="props.order.creditCard.cc_type == 'Visa'" alt="visa-logo"/>
                        <AmexIcon v-if="props.order.creditCard.cc_type.includes(['American Express','AMEX'])" alt="amex-logo"/>
                        <DiscoverIcon v-if="props.order.creditCard.cc_type == 'Discover'" alt="discover-logo"/>
                        <p>{{!['Visa', 'American Express', 'AMEX', 'Discover', 'Master Card', 'MasterCard'].includes(props.order.creditCard.cc_type) ? `Card ` : ""}}•••• {{ props.order.creditCard.cc_last_4 }}</p>
                    </div>
                    <div class="payment-method" v-else>
                        <PaypalIcon v-if="props.order.billing_payment_method == 'PPAL'" alt="paypal-logo" />
                        <p>{{ props.order.billing_payment_method }}</p>
                    </div>
                </div>
                <div class="details-container noborder">
                    <div class="container-header">
                        <p>Billing address</p>
                    </div>
                    <div>
                        <p>{{ props.order.billing_name }}</p>
                        <p>{{ props.order.billing_address }}</p>
                        <p v-if="props.order.billing_address2">{{ props.order.billing_address2 }}</p>
                        <p>{{ props.order.billing_city }}, {{ props.order.billing_state }} {{ props.order.billing_zip }}, {{ props.order.billing_country }}</p>
                    </div>
                </div>
            </div>
            <div v-if="!isLoggedIn" class="main-checkout-guest">
                <div class="checkout-guest-section" style="padding: unset;">
                    <div class="authorization">
                        <form class="authorization-form">
                            <h3 class="title-l" style="margin-bottom: 0;">Save Time on Future Purchases?</h3>
                            <div class="guest-checkout-checkboxes-container">
                                <div class="container-section create-account">
                                    <div class="create-account-container">
                                        <input type="checkbox" id="create_account" aria-label="Quickly create an account" class="mt-checkbox" value="c" v-model="form.quick_account_create">
                                        <p class="paragraph-details"><span class="bold">Quickly create an account</span> without leaving checkout & save time on future orders</p>
                                    </div>
                                    <div class="input-folder"><p class="input-support-text invalid-state" v-if="quickCreateUserErrors">{{quickCreateUserErrors}}</p></div>
                                    <div class="password-form-fields toggle-on-check">
                                        <PasswordInput 
                                            v-model="form.password" 
                                            :errors="form.errors.password" 
                                            id="signupPasswordInput_guest_password"
                                            placeholder="Password *"
                                        />
                                        <PasswordInput 
                                            v-model="form.confirm_password" 
                                            :errors="form.errors.confirm_password" 
                                            id="signupPasswordInput_guest_confirm_password"
                                            placeholder="Confirm Password *"
                                        />
                                    </div>
                                    <div class="flex-row justify-end" style="padding-top:1rem;">
                                        <RothcoCta @click.prevent="createAccount()" :class="form.quick_account_create && !form.hasErrors ? 'primary' : 'disabled'">Create Account</RothcoCta>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <div class="continue-shopping-desktop">
                <RothcoCta :href="route('home')" class="primary">
                    Continue shopping
                </RothcoCta>
                <RothcoCta  :href="route('return.show', [props.order.encrypted_order_id])" class="secondary">
                    Return or Replace Items
                </RothcoCta>
            </div>
        </section>
		<!-- ORDER SUMMARY CONFIRMATION -->
		 <section class="order-confirmation">
            <section class="summary-section">
                <div class="summary-container">
                    <div class="container-header">
                        <h3 id="order-summary-heading" class="headline-s">Order Summary</h3>
                        <p class="text-rothcoBlack quantity" aria-label="Number of items in order">{{ props.order.count }} Items</p>
                    </div>
                    <dl>
                        <dt class="paragraph-default" aria-label="Subtotal amount">Subtotal</dt>
                        <dd class="paragraph-default">{{ summaryContent.formattedSubtotal }}</dd>
                    </dl>
                    <dl v-if="+props.order.discount > 0">
                        <dt class="paragraph-default" aria-label="Discount amount">Discounts</dt>
                        <dd class="paragraph-default text-rothcoRed font-italic">-{{ props.order.discount ? summaryContent.formattedDiscount : '$0.00' }}</dd>
                    </dl>
                    <dl>
                        <dt class="paragraph-default" aria-label="Shipping cost">Shipping</dt>
                        <dd class="paragraph-default">{{ summaryContent.formattedShipping }}</dd>
                    </dl>
                    <dl>
                        <dt class="paragraph-default" aria-label="Taxes">Taxes</dt>
                        <dd class="paragraph-default font-italic" v-if="props.order.sales_tax">{{ summaryContent.formattedTax }}</dd>
                    </dl>
                    <dl class="order-total">
                        <dt class="title-l" aria-label="Total cost">Total</dt>
                        <dd class="title-l">{{ summaryContent.formattedTotal }}</dd>
                    </dl>
                </div>
                <div class="order-products">
                    <h3 class="title-l">Items in Order</h3>
                    <OrderItemsD2CCheckout 
                        :list-items="orderItems"
                        :readonly="true"
                    />
                </div>
                <div class="order-footer-links-desktop">
                    <a :href="helpRoute" target="_blank" aria-label="Get help">Need Help?</a>
                    <a :href="returnPolicyRoute" target="_blank" aria-label="Return policy details">Return policy</a>
                    <a :href="privacyPolicyRoute" target="_blank" aria-label="Privacy policy details">Privacy policy</a>
                    <a :href="termsAndConditionsRoute" target="_blank" aria-label="Terms of service details">Terms of service</a>
                </div>
            </section>
		 </section>
		<div class="continue-shopping-mobile">
			<RothcoCta :href="route('home')" class="primary">
                Continue shopping
            </RothcoCta>
		</div>
	</div>
</template>
<script lang="ts" setup>
    import { computed, ref, onMounted, watch} from 'vue';
    import OrderItemsD2CCheckout from '@/components/OrderItemsD2CCheckout.vue';
    import RothcoCta from '@/components/RothcoCta.vue';
    import { useIntelliSuggest } from '@/helpers/composables/intellisuggest';
    import { useForm } from "@/helpers/composables/useForm";
    import { string, boolean, object} from 'yup';
    import { router } from "@inertiajs/vue3";
    import { useFetch } from '@/helpers/composables/useFetch';
    import { route } from "ziggy-js";
    import { storeToRefs } from "pinia";
    import { useSettingsStore } from "@/store/settings";
    import PasswordInput from "@/components/PasswordInput.vue";
    import MasterCardIcon from "@icons/mastercard.svg";
    import VisaIcon from "@icons/visa.svg";
    import AmexIcon from "@icons/amex.svg";
    import DiscoverIcon from "@icons/discover.svg";
    import PaypalIcon from "@icons/paypal.svg";
    import CheckmarkCircleIcon from "@icons/checkmark-circle.svg";
    import ChevronRedIcon from "@icons/chevronRed.svg";
    import useGoogleManager from '@/helpers/composables/google';
    import { useMetaConversionAPI } from "@/helpers/composables/useMetaConversionAPI";
    let props = defineProps({
        order: {
            type: Object,
            default() {
                return {};
            }
        },
        order_user: {
            type: Object,
            default() {
                return {};
            }
        },
        user_key: {
            type: [Number, String],
            default: null
        },
        isSuccessPage: {
            type: Boolean,
        }
    });

    let user = computed(()=>{
        return props.order.user ?? props.order_user;
    });


    let { gtmPurchaseEvent, gaPurchaseTransaction } = useGoogleManager();

    const { isLoggedIn } = storeToRefs(useSettingsStore());
    let { setNotificationMessage } = useSettingsStore();

    let emailSignup = ref((user.value.news_emails) == 1);
    let smsSignup = ref((user.value.sms_signup) == 1);
    const saveSignupPayload = computed(()=>{
        return {
            user: {
                news_emails: emailSignup.value ? 1 : 0,
                sms_signup: smsSignup.value ? 1 : 0,
                phone_number: props.order?.billing_phone
            }
        }
    })

    let { execute: update, canAbort, abort } = useFetch(isLoggedIn.value ? route('endpoint.user.update', user.value?.user_id) : route('endpoint.guestuser.update', props.user_key), {
        immediate: false,
        beforeFetch: (ctx) => {
            ctx.options.headers = {
                ...ctx.options.headers,
                Accept: 'application/json',
            }
            return ctx
        },
        afterFetch: (response) => {
            if (response.response.ok) {
                setNotificationMessage({
                    message: `Settings Updated`,
                    type: 'success'
                })
            }
        }
    }).put(()=>(saveSignupPayload.value)).json()

    let saveSignup = () => {
        if(canAbort.value){
            abort();
        }
        update();
    };

    let summaryContent = computed(()=>{
        let formattedSubtotal = !props.order.subtotal ? `$0.00` : `$${parseFloat(props.order.subtotal.toString()).toFixed(2)}`
        let formattedTotal = !props.order.total ? `$0.00` : `$${parseFloat(props.order.total.toString()).toFixed(2)}`;
        let formattedShipping = !props.order.shipping_charge ? `$0.00` : `$${parseFloat(props.order.shipping_charge.toString()).toFixed(2)}`;
        let formattedDiscount = !props.order.discount ? `$0.00` : `$${parseFloat(props.order.discount.toString()).toFixed(2)}`;
        let formattedTax = !props.order.sales_tax ? `$0.00` : `$${parseFloat(props.order.sales_tax.toString()).toFixed(2)}`;
        return { formattedSubtotal, formattedTotal, formattedShipping, formattedDiscount, formattedTax }
    });

    let orderItems = computed(()=>{
        return props.order.order_items.map(order_item=>{
            return {...order_item, variation: order_item.item_variation}
        })
    });

    let termsAndConditionsRoute = computed(()=>route('index.cms', 'rothco-terms-conditions'));
    let privacyPolicyRoute = computed(()=>route('index.cms', 'rothco-privacy-policy'));
    let returnPolicyRoute = computed(()=>route('customer-service.cms', 'consumer-return-policy'));
    let helpRoute = computed(()=>route('customer-service.index'));


    let { sale } = useIntelliSuggest({
        siteId: 'xxkp6b',
        shopperId: user.value.user_id ?? undefined
    });

    const loginPlaceholdersHidden = ref(new Map());
    function toggleLoginPlaceholder(id) {
        if(!loginPlaceholdersHidden.value.has(id)) {
            loginPlaceholdersHidden.value.set(id, true);
        }else{
            loginPlaceholdersHidden.value.set(id, !loginPlaceholdersHidden.value.get(id));
        }
    }

    const form = useForm({
        useInertia: true,
        // inertiaAlias: "D2CCheckout",
        schema: object({
            quick_account_create: boolean().notRequired().default(false),
            password: string().when('quick_account_create', {
                is: true,
                then: ()=> string().required('The password field is required').min(8, 'Password must be at least 8 characters')
                    .test('uppercase', 'Password must contain at least one uppercase letter', (value) => {
                        return /[A-Z]/.test(value);
                    }).test('lowercase', 'Password must contain at least one lowercase letter', (value) => {
                        return /[a-z]/.test(value);
                    }).test('number', 'Password must contain at least one number', (value) => {
                        return /[0-9]/.test(value);
                    }).test('special', 'Password must contain at least one special character (# ? ! @ $  % ^ & * -)', (value) => {
                        return /[#?!@$%^&*-]/.test(value);
                    }).test('spaces', 'Password must not contain spaces', (value) => {
                        return !/\s/.test(value);
                    }).test('weak', 'Password is too weak', (value) => {
                        return !/password/i.test(value);
                    }).default(""),
                otherwise: ()=> string().notRequired()
            }),
            confirm_password: string().when('quick_account_create', {
                is: true,
                then: ()=> string().required('Passwords must match').test('match', 'Passwords must match', (value)=>{
                    return value === form.password;
                }),
                otherwise: ()=> string().notRequired()
            }),
        })
    });

    const quickCreateUserErrors = ref(null);

    function createAccount() {
        quickCreateUserErrors.value = null;

        if(!form.hasErrors){
            useFetch(
                route('b2c.create.account'),
                {
                    beforeFetch(ctx) {
                        ctx.options.headers = {
                            ...ctx.options.headers,
                            'Accept': 'application/json',
                        }
                        return ctx;
                    },
                    afterFetch(ctx) {
                        if(ctx.data.errors){
                            quickCreateUserErrors.value = ctx.data.errors;
                        }else{
                            router.visit(route(route().current()),{});
                        }
                    },
                    onFetchError(ctx) {
                        if(ctx.data.errors){
                            quickCreateUserErrors.value = ctx.data.errors;
                        }
                        return ctx;
                    }
                }
            )
            .post({
                'login-email': props.order.billing_email,
                'login-password': form.password,
                'confirm-password': form.confirm_password
            })
            .json();
        } 
    }

    const displayName = computed(()=>{
        return user.value ? user.value.username : props.order.billing_name;
    })
    let { metaPurchase } = useMetaConversionAPI();
    onMounted(()=>{
        if(props.isSuccessPage){
            sale({
                orderId: props.order.order_id,
                total: props.order.total,
                items: props.order.order_items.map(order_item=>{
                    return {
                    sku: order_item.item_variation.item?.product_line_code,
                        qty: order_item.quantity,
                        price: order_item.price,
                        childSku: order_item.item_variation.rothcoItem?.rothco_item_no
                    }
                }),
                city: props.order.shipping_city,
                state: props.order.shipping_state,
                country: props.order.shipping_country,
            });
            gtmPurchaseEvent({
                ...props.order,
                items: props.order.order_items.map(order_item=>{
                    return {
                        ...order_item,
                        variation: order_item.item_variation,
                    }
                }),
            });

            gaPurchaseTransaction({
                ...props.order,
                items: props.order.order_items.map(order_item=>{
                    return {
                        ...order_item,
                        variation: order_item.item_variation,
                    }
                }),
            });
            
            metaPurchase(props.order.order_id)
        }   
        
    });
</script>